import type { ToolUIPart } from "ai";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { isValidElement } from "react";

import { type IgThemeType, IgTypography } from "@intelligems/ig-theme";
import { smartJsonParse } from "@intelligems/ig-utils";
import { useTheme } from "@mui/material";
import { Box } from "@mui/system";
import Stack from "@mui/material/Stack";
import { Badge } from "@src/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/components/ui/collapsible";
import { ScreenshotEvidenceImage } from "@src/components/ai-elements/screenshot-evidence-image";
import { cn } from "@src/lib/utils";

import { CodeBlock } from "./code-block";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn(
      "not-prose mb-4 w-full max-w-full overflow-hidden rounded-md border",
      className
    )}
    {...props}
  />
);

export type ToolHeaderProps = {
  title?: string;
  type: ToolUIPart["type"];
  state: ToolUIPart["state"];
  isExpandable?: boolean;
  className?: string;
  compact?: boolean;
  /**
   * Leading icon. Omit for the wrench; pass `null` for none.
   *
   * `undefined` and `null` have to mean different things here — a surface that
   * wants no icon has to be able to say so, and leaving the prop off must keep
   * the wrench every existing tool card already draws.
   */
  icon?: ReactNode;
  /**
   * The state pill beside the title. Omit for the default pill, `null` for
   * none. Same three-way meaning as `icon`, so a caller learns it once.
   */
  status?: ReactNode;
};

type StatusBadgeStyle = Pick<
  CSSProperties,
  "backgroundColor" | "borderColor" | "color"
>;

const getStatusBadgeStyle = (
  theme: IgThemeType,
  status: ToolUIPart["state"]
): StatusBadgeStyle => {
  const styles = {
    "input-streaming": {
      backgroundColor: theme.igBackgroundColors.interactive.neutral.default,
      borderColor: theme.igBorders.core.default,
      color: theme.palette.text.secondary,
    },
    "input-available": {
      backgroundColor: theme.igBackgroundColors.tonal.info,
      borderColor: theme.igBorders.info.default,
      color: theme.palette.text.tonal.info,
    },
    "approval-requested": {
      backgroundColor: theme.igBackgroundColors.tonal.warning,
      borderColor: theme.igBorders.warning.default,
      color: theme.palette.text.tonal.warning,
    },
    "approval-responded": {
      backgroundColor: theme.igBackgroundColors.tonal.info,
      borderColor: theme.igBorders.info.default,
      color: theme.palette.text.tonal.info,
    },
    "output-available": {
      backgroundColor: theme.igBackgroundColors.tonal.success,
      borderColor: theme.igBorders.success.default,
      color: theme.palette.text.tonal.success,
    },
    "output-denied": {
      backgroundColor: theme.igBackgroundColors.tonal.error,
      borderColor: theme.igBorders.error.default,
      color: theme.palette.text.tonal.error,
    },
    "output-error": {
      backgroundColor: theme.igBackgroundColors.tonal.error,
      borderColor: theme.igBorders.error.default,
      color: theme.palette.text.tonal.error,
    },
  } as const satisfies Record<ToolUIPart["state"], StatusBadgeStyle>;

  return styles[status];
};

const getStatusBadge = (
  theme: IgThemeType,
  status: ToolUIPart["state"],
  compact?: boolean
) => {
  const labels = {
    "input-streaming": "Pending",
    "input-available": "Running",
    "approval-requested": "Approval needed",
    "approval-responded": "Approval sent",
    "output-available": "Completed",
    "output-denied": "Denied",
    "output-error": "Error",
  } as const satisfies Record<ToolUIPart["state"], string>;

  const statusBadgeStyle = getStatusBadgeStyle(theme, status);
  const icons = {
    "input-streaming": (
      <CircleIcon className="size-4" color={statusBadgeStyle.color} />
    ),
    "input-available": (
      <ClockIcon
        className="size-4 animate-pulse"
        color={statusBadgeStyle.color}
      />
    ),
    "approval-requested": (
      <ClockIcon className="size-4" color={statusBadgeStyle.color} />
    ),
    "approval-responded": (
      <CheckCircleIcon className="size-4" color={statusBadgeStyle.color} />
    ),
    "output-available": (
      <CheckCircleIcon className="size-4" color={statusBadgeStyle.color} />
    ),
    "output-denied": (
      <XCircleIcon className="size-4" color={statusBadgeStyle.color} />
    ),
    "output-error": (
      <XCircleIcon className="size-4" color={statusBadgeStyle.color} />
    ),
  } as const satisfies Record<ToolUIPart["state"], ReactNode>;

  return (
    <Badge
      className={cn(
        "gap-1.5 rounded-full text-xs",
        compact ? "px-1.5 py-0 text-[10px]" : undefined
      )}
      style={statusBadgeStyle}
      variant="outline"
    >
      {icons[status]}
      {labels[status]}
    </Badge>
  );
};

export const ToolHeader = ({
  className,
  title,
  type,
  state,
  isExpandable,
  compact,
  icon,
  status,
  ...props
}: ToolHeaderProps) => {
  const theme = useTheme<IgThemeType>();

  return (
    <CollapsibleTrigger
      className={cn(
        "flex w-full min-w-0 items-center justify-between gap-4 overflow-hidden p-3",
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 items-center gap-2">
        {icon === undefined ? (
          <WrenchIcon className="size-4 text-muted-foreground" />
        ) : (
          icon
        )}
        <IgTypography
          fontSize={"12px"}
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title ?? type.split("-").slice(1).join("-")}
        </IgTypography>
        {status === undefined ? getStatusBadge(theme, state, compact) : status}
      </div>
      {isExpandable === false ? null : (
        <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      )}
    </CollapsibleTrigger>
  );
};

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className
    )}
    {...props}
  />
);

const SectionLabel = ({
  children,
  compact,
}: {
  children: ReactNode;
  compact?: boolean;
}) => (
  <Box
    pl={2}
    component="h4"
    className={cn(
      "font-medium text-muted-foreground text-xs uppercase tracking-wide",
      compact ? "text-[10px]" : undefined
    )}
  >
    {children}
  </Box>
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolUIPart["input"];
  compact?: boolean;
};

export const ToolInput = ({
  className,
  input,
  compact,
  ...props
}: ToolInputProps) => (
  <div
    className={cn("w-full min-w-0 space-y-2 overflow-hidden p-4", className)}
    {...props}
  >
    <Box
      className={cn(
        "rounded-md bg-muted/50 ig-code-block",
        compact ? "text-[11px]" : undefined
      )}
      sx={{ maxWidth: "100%", overflowX: "auto" }}
    >
      <CodeBlock
        code={input}
        language="json"
        title={<SectionLabel compact={compact}>Parameters</SectionLabel>}
      />
    </Box>
  </div>
);

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolUIPart["output"];
  errorText: ToolUIPart["errorText"];
  compact?: boolean;
  imageDescriptionFallback?: string;
};

// Tool results from the MCP layer wrap the real payload in a `{ type, value }`
// envelope (often with `value` itself a JSON string). Parse and unwrap it so the
// editor shows the actual result, not the transport envelope.
const normalizeToolOutput = (output: ToolUIPart["output"]): unknown => {
  const parsed = smartJsonParse(output);

  if (
    parsed !== null &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    "value" in parsed
  ) {
    return (parsed as { value: unknown }).value;
  }

  return parsed;
};

interface RenderableToolImage {
  description: string;
  href?: string | null;
  key: string;
  src: string;
  surface?: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getString = (
  record: Record<string, unknown>,
  key: string
): string | undefined => {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const getNullableString = (
  record: Record<string, unknown>,
  key: string
): string | null | undefined => {
  const value = record[key];
  if (value === null) return null;
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const getImageFromRecord = (
  record: Record<string, unknown>,
  fallbackDescription: string
): RenderableToolImage | null => {
  if (record.type === "image" && isRecord(record.source)) {
    const data = getString(record.source, "data");
    const mediaType = getString(record.source, "media_type");
    const sourceType = getString(record.source, "type");

    if (data && mediaType?.startsWith("image/") && sourceType === "base64") {
      return {
        description: fallbackDescription,
        key: `base64-${mediaType}-${data.slice(0, 32)}`,
        src: `data:${mediaType};base64,${data}`,
      };
    }
  }

  if (record.type === "media") {
    const data = getString(record, "data");
    const mediaType = getString(record, "mediaType");

    if (data && mediaType?.startsWith("image/")) {
      return {
        description: fallbackDescription,
        key: `media-${mediaType}-${data.slice(0, 32)}`,
        src: `data:${mediaType};base64,${data}`,
      };
    }
  }

  const imageKey = getString(record, "imageKey");
  const imageUrl = getString(record, "imageUrl");
  if (imageKey && imageUrl) {
    return {
      description: getString(record, "description") ?? fallbackDescription,
      href: imageUrl,
      key: imageKey,
      src: imageUrl,
      surface: getNullableString(record, "surface") ?? null,
    };
  }

  return null;
};

const collectToolOutputImages = (
  value: unknown,
  fallbackDescription: string
): RenderableToolImage[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) =>
      collectToolOutputImages(item, fallbackDescription)
    );
  }

  if (!isRecord(value)) {
    return [];
  }

  const image = getImageFromRecord(value, fallbackDescription);
  if (image) {
    return [image];
  }

  const nestedValues = ["content", "value"].flatMap((key) =>
    Object.prototype.hasOwnProperty.call(value, key)
      ? collectToolOutputImages(value[key], fallbackDescription)
      : []
  );

  return nestedValues;
};

const stripRenderedImages = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const nextValue = value
      .map((item) => stripRenderedImages(item))
      .filter((item) => item !== undefined);

    return nextValue.length > 0 ? nextValue : undefined;
  }

  if (!isRecord(value)) {
    return value;
  }

  if (getImageFromRecord(value, "")) {
    return undefined;
  }

  const nextEntries = Object.entries(value)
    .map(([key, entryValue]) => [key, stripRenderedImages(entryValue)] as const)
    .filter(([, entryValue]) => entryValue !== undefined);

  return nextEntries.length > 0 ? Object.fromEntries(nextEntries) : undefined;
};

const ToolOutputBody = ({
  value,
  errorText,
  compact,
  title,
}: {
  value: unknown;
  errorText: ToolUIPart["errorText"];
  compact?: boolean;
  title: ReactNode;
}) => {
  if (
    (typeof value === "object" && !isValidElement(value)) ||
    typeof value === "string"
  ) {
    return <CodeBlock code={value} language="json" title={title} />;
  }

  return <Box>{value as ReactNode}</Box>;
};

export const ToolOutput = ({
  className,
  output,
  errorText,
  compact,
  imageDescriptionFallback = "Screenshot captured by tool",
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  const value = errorText ?? normalizeToolOutput(output);
  const title = (
    <SectionLabel compact={compact}>
      {errorText ? "Error" : "Result"}
    </SectionLabel>
  );
  const images = errorText
    ? []
    : collectToolOutputImages(value, imageDescriptionFallback);
  const nonImageValue = images.length > 0 ? stripRenderedImages(value) : value;

  return (
    <div className={cn("w-full min-w-0 space-y-2 p-4", className)} {...props}>
      {nonImageValue !== undefined ? (
        <Box
          sx={{ maxWidth: "100%", overflowX: "auto" }}
          className={cn(
            "w-full max-w-full overflow-x-auto rounded-md text-xs [&_table]:w-full ig-code-block",
            compact ? "text-[11px]" : undefined,
            errorText
              ? "bg-destructive/10 text-destructive"
              : "bg-muted/50 text-foreground"
          )}
        >
          <ToolOutputBody
            value={nonImageValue}
            errorText={errorText}
            compact={compact}
            title={title}
          />
        </Box>
      ) : null}
      {images.length > 0 ? (
        <Stack direction="column" spacing={1}>
          {images.map((image) => (
            <ScreenshotEvidenceImage
              description={image.description}
              href={image.href}
              key={image.key}
              maxHeight={compact ? "220px" : "320px"}
              src={image.src}
              surface={image.surface}
            />
          ))}
        </Stack>
      ) : null}
    </div>
  );
};
